// src/components/EducationLoan.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './EducationLoan.css';

const EducationLoan = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('domestic');
  const [loanAmount, setLoanAmount] = useState(500000);
  const [loanTenure, setLoanTenure] = useState(5);
  const [interestRate] = useState(8.5);

  const handleGoBack = () => {
    navigate('/home');
  };

  const handleApplyNow = (bankName) => {
    alert(`Application initiated for ${bankName}. Our representative will contact you shortly.`);
  };

  const calculateEMI = () => {
    const rate = interestRate / 100 / 12; // Monthly interest rate
    const months = loanTenure * 12;
    const emi = (loanAmount * rate * Math.pow(1 + rate, months)) / 
                (Math.pow(1 + rate, months) - 1);
    return Math.round(emi);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const totalPayment = calculateEMI() * loanTenure * 12;
  const totalInterest = totalPayment - loanAmount;

  return (
    <div className="education-container">
      {/* Professional Back Button */}
      {/* <button className="education-back-btn" onClick={handleGoBack}>
        <span className="education-back-arrow">←</span>
        <span className="education-back-text">Back to Home</span>
      </button> */}

      {/* Hero Section */}
      <div className="education-hero-section">
        <div className="education-hero-content">
          <h1 className="education-hero-title">
            <span className="education-gradient-text">Education Loan</span>
            <br />
            for Your Bright Future
          </h1>
          <p className="education-hero-subtitle">
            Finance your education dreams with attractive interest rates, flexible repayment, 
            and tax benefits under Section 80E
          </p>
          
          <div className="education-stats-container">
            <div className="education-stat-card">
              <div className="education-stat-icon">₹</div>
              <div className="education-stat-content">
                <div className="education-stat-value">50 Lakhs</div>
                <div className="education-stat-label">Max Loan Amount</div>
              </div>
            </div>
            <div className="education-stat-card">
              <div className="education-stat-icon">%</div>
              <div className="education-stat-content">
                <div className="education-stat-value">8.5%*</div>
                <div className="education-stat-label">Interest Rate</div>
              </div>
            </div>
            <div className="education-stat-card">
              <div className="education-stat-icon">📅</div>
              <div className="education-stat-content">
                <div className="education-stat-value">15 Years</div>
                <div className="education-stat-label">Max Tenure</div>
              </div>
            </div>
            <div className="education-stat-card">
              <div className="education-stat-icon">🎯</div>
              <div className="education-stat-content">
                <div className="education-stat-value">100%</div>
                <div className="education-stat-label">Course Fee Covered</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Section */}
      <div className="education-calculator-section">
        <div className="education-section-header">
          <h2><span className="education-header-icon">💰</span> Education Loan Calculator</h2>
          <p>Calculate your EMI and plan your education financing</p>
        </div>
        
        <div className="education-calculator-wrapper">
          <div className="education-calculator-inputs">
            <div className="education-input-group">
              <label>
                <span className="education-input-label">Loan Amount</span>
                <span className="education-amount-display">{formatCurrency(loanAmount)}</span>
              </label>
              <input
                type="range"
                min="100000"
                max="5000000"
                step="50000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                className="education-custom-slider"
              />
              <div className="education-slider-labels">
                <span>₹1 Lakh</span>
                <span>₹50 Lakhs</span>
              </div>
            </div>

            <div className="education-input-group">
              <label>
                <span className="education-input-label">Tenure Period</span>
                <span className="education-tenure-display">{loanTenure} Years</span>
              </label>
              <input
                type="range"
                min="1"
                max="15"
                step="1"
                value={loanTenure}
                onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                className="education-custom-slider"
              />
              <div className="education-slider-labels">
                <span>1 Year</span>
                <span>15 Years</span>
              </div>
            </div>

            <div className="education-input-group">
              <label>
                <span className="education-input-label">Interest Rate</span>
                <span className="education-rate-display">{interestRate}% p.a.</span>
              </label>
              <div className="education-rate-indicator">
                <div className="education-rate-bar" style={{ width: `${(interestRate / 15) * 100}%` }}></div>
              </div>
            </div>
          </div>

          <div className="education-calculator-results">
            <div className="education-result-card">
              <div className="education-result-header">
                <h3>Estimated Monthly EMI</h3>
              </div>
              <div className="education-result-amount">
                {formatCurrency(calculateEMI())}
              </div>
              <div className="education-result-details">
                <div className="education-detail-item">
                  <span className="education-detail-label">Principal Amount</span>
                  <span className="education-detail-value">{formatCurrency(loanAmount)}</span>
                </div>
                <div className="education-detail-item">
                  <span className="education-detail-label">Total Interest</span>
                  <span className="education-detail-value">{formatCurrency(totalInterest)}</span>
                </div>
                <div className="education-detail-item">
                  <span className="education-detail-label">Total Payment</span>
                  <span className="education-detail-value education-total-payment">{formatCurrency(totalPayment)}</span>
                </div>
              </div>
              <button className="education-calculate-btn">
                Apply for Loan
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loan Types Section */}
      <div className="education-loan-types-section">
        <div className="education-section-header">
          <h2><span className="education-header-icon">🎯</span> Choose Your Loan Type</h2>
        </div>
        
        <div className="education-tabs-container">
          <div className="education-tabs-header">
            <button 
              className={`education-tab-button ${activeTab === 'domestic' ? 'education-active' : ''}`}
              onClick={() => setActiveTab('domestic')}
            >
              <span className="education-tab-icon">🇮🇳</span>
              Domestic Education
            </button>
            <button 
              className={`education-tab-button ${activeTab === 'abroad' ? 'education-active' : ''}`}
              onClick={() => setActiveTab('abroad')}
            >
              <span className="education-tab-icon">✈️</span>
              Study Abroad
            </button>
          </div>
          <div className="education-tab-content">
            {activeTab === 'domestic' ? (
              <div className="education-tab-panel">
                <div className="education-feature-highlights">
                  <div className="education-feature-card">
                    <div className="education-feature-icon">🏛️</div>
                    <h4>For Indian Institutions</h4>
                    <ul>
                      <li>IITs, IIMs, NITs</li>
                      <li>Medical & Law Colleges</li>
                      <li>All UGC recognized institutes</li>
                    </ul>
                  </div>
                  <div className="education-feature-card">
                    <div className="education-feature-icon">💰</div>
                    <h4>Loan Coverage</h4>
                    <ul>
                      <li>Up to ₹20 Lakhs without collateral</li>
                      <li>100% tuition fee coverage</li>
                      <li>Living expenses included</li>
                    </ul>
                  </div>
                  <div className="education-feature-card">
                    <div className="education-feature-icon">⏰</div>
                    <h4>Repayment Benefits</h4>
                    <ul>
                      <li>Course period + 6 months moratorium</li>
                      <li>Flexible repayment options</li>
                      <li>Tax benefits available</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="education-tab-panel">
                <div className="education-feature-highlights">
                  <div className="education-feature-card">
                    <div className="education-feature-icon">🌎</div>
                    <h4>Global Coverage</h4>
                    <ul>
                      <li>USA, UK, Canada, Australia</li>
                      <li>Europe & Asian countries</li>
                      <li>30+ countries supported</li>
                    </ul>
                  </div>
                  <div className="education-feature-card">
                    <div className="education-feature-icon">💵</div>
                    <h4>Financial Support</h4>
                    <ul>
                      <li>Up to ₹1 Crore loan amount</li>
                      <li>Competitive forex rates</li>
                      <li>Travel & insurance coverage</li>
                    </ul>
                  </div>
                  <div className="education-feature-card">
                    <div className="education-feature-icon">🏠</div>
                    <h4>Living Expenses</h4>
                    <ul>
                      <li>Accommodation coverage</li>
                      <li>Study material allowance</li>
                      <li>Travel ticket financing</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bank Offers */}
      <div className="education-bank-offers-section">
        <div className="education-section-header">
          <h2><span className="education-header-icon">🏦</span> Compare Top Bank Offers</h2>
          <p>Select from the best education loan providers</p>
        </div>
        
        <div className="education-bank-cards">
          <div className="education-bank-card">
            <div className="education-bank-header" style={{ background: '#0033a0' }}>
              <div className="education-bank-logo">SBI</div>
              <h3>State Bank of India</h3>
            </div>
            <div className="education-bank-details">
              <div className="education-detail-row">
                <span>Interest Rate</span>
                <span className="education-highlight-rate">8.55% - 11.15%</span>
              </div>
              <div className="education-detail-row">
                <span>Processing Fee</span>
                <span>₹10,000</span>
              </div>
              <div className="education-detail-row">
                <span>Max Amount</span>
                <span>₹50 Lakhs</span>
              </div>
              <div className="education-detail-row">
                <span>EMI (for ₹5L)</span>
                <span className="education-emi-amount">₹10,300</span>
              </div>
              <button onClick={() => handleApplyNow('SBI')} className="education-apply-now-btn" style={{ background: '#0033a0' }}>
                Apply Now
              </button>
            </div>
          </div>
          <div className="education-bank-card">
            <div className="education-bank-header" style={{ background: '#004a97' }}>
              <div className="education-bank-logo">HDFC</div>
              <h3>HDFC Bank</h3>
            </div>
            <div className="education-bank-details">
              <div className="education-detail-row">
                <span>Interest Rate</span>
                <span className="education-highlight-rate">9.50% - 13.00%</span>
              </div>
              <div className="education-detail-row">
                <span>Processing Fee</span>
                <span>1%</span>
              </div>
              <div className="education-detail-row">
                <span>Max Amount</span>
                <span>₹1 Crore</span>
              </div>
              <div className="education-detail-row">
                <span>EMI (for ₹5L)</span>
                <span className="education-emi-amount">₹10,500</span>
              </div>
              <button onClick={() => handleApplyNow('HDFC')} className="education-apply-now-btn" style={{ background: '#004a97' }}>
                Apply Now
              </button>
            </div>
          </div>
          <div className="education-bank-card">
            <div className="education-bank-header" style={{ background: '#b80000' }}>
              <div className="education-bank-logo">ICICI</div>
              <h3>ICICI Bank</h3>
            </div>
            <div className="education-bank-details">
              <div className="education-detail-row">
                <span>Interest Rate</span>
                <span className="education-highlight-rate">9.50% - 12.50%</span>
              </div>
              <div className="education-detail-row">
                <span>Processing Fee</span>
                <span>0.5% - 2%</span>
              </div>
              <div className="education-detail-row">
                <span>Max Amount</span>
                <span>₹50 Lakhs</span>
              </div>
              <div className="education-detail-row">
                <span>EMI (for ₹5L)</span>
                <span className="education-emi-amount">₹10,450</span>
              </div>
              <button onClick={() => handleApplyNow('ICICI')} className="education-apply-now-btn" style={{ background: '#b80000' }}>
                Apply Now
              </button>
            </div>
          </div>
          <div className="education-bank-card">
            <div className="education-bank-header" style={{ background: '#1c1c1c' }}>
              <div className="education-bank-logo">AXIS</div>
              <h3>Axis Bank</h3>
            </div>
            <div className="education-bank-details">
              <div className="education-detail-row">
                <span>Interest Rate</span>
                <span className="education-highlight-rate">13.70% - 15.20%</span>
              </div>
              <div className="education-detail-row">
                <span>Processing Fee</span>
                <span>1.5% - 2%</span>
              </div>
              <div className="education-detail-row">
                <span>Max Amount</span>
                <span>₹75 Lakhs</span>
              </div>
              <div className="education-detail-row">
                <span>EMI (for ₹5L)</span>
                <span className="education-emi-amount">₹11,200</span>
              </div>
              <button onClick={() => handleApplyNow('Axis')} className="education-apply-now-btn" style={{ background: '#1c1c1c' }}>
                Apply Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="education-cta-section">
        <div className="education-cta-content">
          <h2>Ready to Fund Your Education?</h2>
          <p>Get personalized loan offers with minimal documentation and quick disbursal</p>
          <div className="education-cta-buttons">
            <button className="education-cta-btn education-primary">
              Apply Online Now
            </button>
            <button className="education-cta-btn education-secondary">
              Compare More Options
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationLoan;